import * as React from "react"
import { CalendarDays, Check, Clock3, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { DateSelectionCalendar } from "@/components/ui/date-selection-calendar"
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

const pad = (value) => String(value).padStart(2, "0")

function parseDate(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return undefined
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12)
  return Number.isNaN(date.getTime()) ? undefined : date
}

function isoDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function valueTime(value, type) {
  const raw = String(value || "")
  const match = raw.match(type === "datetime-local" ? /T(\d{2}):(\d{2})/ : /^(\d{2}):(\d{2})/)
  return match ? `${match[1]}:${match[2]}` : ""
}

function displayValue(value, type) {
  if (!value) return ""
  if (type === "time") return valueTime(value, type)
  const date = parseDate(value)
  if (!date) return ""
  const formattedDate = new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date)
  return type === "datetime-local" ? `${formattedDate}, ${valueTime(value, type) || "00:00"}` : formattedDate
}

function validDateParts(day, month, year) {
  const date = new Date(year, month - 1, day, 12)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : undefined
}

function parseTypedTime(text, allowPartial = false) {
  const raw = String(text || "").trim()
  const separated = raw.match(/^(\d{1,2})\s*:\s*(\d{2})$/)
  const digits = raw.replace(/\D/g, "")
  let hours
  let minutes
  if (separated) {
    hours = Number(separated[1])
    minutes = Number(separated[2])
  } else if (digits.length === 3 || digits.length === 4) {
    hours = Number(digits.slice(0, -2))
    minutes = Number(digits.slice(-2))
  } else if (allowPartial && digits.length >= 1 && digits.length <= 2) {
    hours = Number(digits)
    minutes = 0
  } else {
    return ""
  }
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59
    ? `${pad(hours)}:${pad(minutes)}`
    : ""
}

function parseTypedDate(text) {
  const raw = String(text || "").trim()
  const separated = raw.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/)
  const digits = raw.replace(/\D/g, "")
  const parts = separated
    ? [Number(separated[1]), Number(separated[2]), Number(separated[3])]
    : digits.length === 8
      ? [Number(digits.slice(0, 2)), Number(digits.slice(2, 4)), Number(digits.slice(4))]
      : null
  if (!parts) return ""
  const date = validDateParts(parts[0], parts[1], parts[2])
  return date ? isoDate(date) : ""
}

function parseTypedValue(text, type, allowPartialTime = false) {
  if (type === "time") return parseTypedTime(text, allowPartialTime)
  if (type === "date") return parseTypedDate(text)
  const raw = String(text || "").trim()
  const match = raw.match(/^(.+?)(?:,|\s)\s*(\d{1,2}:?\d{2})$/)
  if (!match) return ""
  const date = parseTypedDate(match[1])
  const time = parseTypedTime(match[2], allowPartialTime)
  return date && time ? `${date}T${time}` : ""
}

const DateTimeInput = React.forwardRef(({
  type,
  value = "",
  onChange,
  className,
  placeholder,
  disabled,
  min,
  max,
  step: _step,
  name,
  id,
  required,
  onBlur,
  onFocus,
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState(() => displayValue(value, type))
  const focusedRef = React.useRef(false)
  const anchorRef = React.useRef(null)
  const selectedDate = parseDate(value)
  const currentTime = valueTime(value, type)
  const [hour, minute] = currentTime ? currentTime.split(":") : ["09", "00"]
  const isDate = type === "date" || type === "datetime-local"

  React.useEffect(() => {
    if (!focusedRef.current) setDraft(displayValue(value, type))
  }, [type, value])

  const emit = (nextValue) => {
    onChange?.({
      target: { value: nextValue, name },
      currentTarget: { value: nextValue, name },
    })
  }

  const handleDraftChange = (event) => {
    const nextDraft = event.target.value
    setDraft(nextDraft)
    if (!nextDraft.trim()) {
      emit("")
      return
    }
    const parsed = parseTypedValue(nextDraft, type)
    if (parsed) emit(parsed)
  }

  const handleBlur = (event) => {
    focusedRef.current = false
    const parsed = parseTypedValue(draft, type, true)
    if (parsed && parsed !== value) emit(parsed)
    setDraft(parsed ? displayValue(parsed, type) : displayValue(value, type))
    onBlur?.(event)
  }

  const selectDate = (date) => {
    if (!date) return
    const nextDate = isoDate(date)
    emit(type === "datetime-local" ? `${nextDate}T${currentTime || "09:00"}` : nextDate)
    if (type === "date") setOpen(false)
  }

  const selectTime = (nextHour, nextMinute, close = false) => {
    const nextTime = `${nextHour}:${nextMinute}`
    if (type === "datetime-local") {
      emit(`${selectedDate ? isoDate(selectedDate) : isoDate(new Date())}T${nextTime}`)
    } else {
      emit(nextTime)
    }
    if (close) setOpen(false)
  }

  const minDate = parseDate(min)
  const maxDate = parseDate(max)
  const disabledDays = [
    minDate ? { before: minDate } : null,
    maxDate ? { after: maxDate } : null,
  ].filter(Boolean)
  const label = displayValue(value, type)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <input type="hidden" name={name} value={value || ""} disabled={disabled} readOnly />
      <PopoverAnchor asChild>
        <div ref={anchorRef} className="relative min-w-0 w-full">
          <input
          ref={ref}
          type="text"
          inputMode="numeric"
          id={id}
          value={focusedRef.current ? draft : label}
          onChange={handleDraftChange}
          onFocus={(event) => {
            focusedRef.current = true
            setDraft(label)
            setOpen(true)
            event.currentTarget.select()
            onFocus?.(event)
          }}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          placeholder={placeholder || (type === "time" ? "HH:MM" : type === "date" ? "TT.MM.JJJJ" : "TT.MM.JJJJ, HH:MM")}
          className={cn(
            "h-9 w-full rounded-xl border border-input bg-white px-3 py-1 pr-10 text-left text-base shadow-sm ring-inset transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--app-accent-ring)] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            className
          )}
          {...props}
          />
          <PopoverTrigger asChild>
            <button type="button" disabled={disabled} aria-label={isDate ? "Kalender öffnen" : "Uhrzeitauswahl öffnen"} className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-xl text-[#6B7280] hover:text-black focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--app-accent-ring)]">
              {isDate ? <CalendarDays className="h-4 w-4" /> : <Clock3 className="h-4 w-4" />}
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        collisionPadding={16}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onFocusOutside={(event) => {
          if (anchorRef.current?.contains(event.target)) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target)) event.preventDefault()
        }}
        className="z-[170] w-auto max-w-[calc(100vw-2rem)] max-h-[min(36rem,var(--radix-popover-content-available-height))] overflow-y-auto overscroll-contain rounded-2xl border-[#E5E5EA] bg-white p-3 shadow-2xl"
      >
        <div className={cn(type === "datetime-local" && "sm:flex sm:w-fit sm:items-stretch")}>
          {isDate ? (
            <DateSelectionCalendar
              selected={selectedDate}
              onSelect={selectDate}
              disabled={disabledDays}
              minDate={minDate}
              maxDate={maxDate}
            />
          ) : null}
          {type !== "date" ? (
            <div className={cn("grid w-fit grid-cols-2 gap-2", type === "datetime-local" && "mt-3 border-t pt-3 sm:ml-3 sm:mt-0 sm:border-l sm:border-t-0 sm:pl-3 sm:pt-0")}>
              <TimeColumn
                label="Stunde"
                values={Array.from({ length: 24 }, (_, index) => pad(index))}
                selected={hour}
                onSelect={(nextHour) => selectTime(nextHour, minute)}
              />
              <TimeColumn
                label="Minute"
                values={Array.from({ length: 60 }, (_, index) => pad(index))}
                selected={minute}
                onSelect={(nextMinute) => selectTime(hour, nextMinute, true)}
              />
            </div>
          ) : null}
        </div>
        {value ? (
          <button type="button" onClick={() => { emit(""); setOpen(false) }} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium text-[#6B7280] hover:bg-[#F5F5F7]">
            <X className="h-3.5 w-3.5" /> Auswahl löschen
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  )
})
DateTimeInput.displayName = "DateTimeInput"

function TimeColumn({ label, values, selected, onSelect }) {
  const selectedRef = React.useRef(null)
  const adjustingScrollRef = React.useRef(false)
  const loopedValues = [0, 1, 2].flatMap((cycle) => values.map((item) => ({ cycle, item })))

  React.useEffect(() => {
    const row = selectedRef.current
    const scroller = row?.parentElement
    if (row && scroller) scroller.scrollTop = row.offsetTop - scroller.offsetTop - (scroller.clientHeight - row.offsetHeight) / 2
  }, [selected])

  const keepScrollLooping = (event) => {
    if (adjustingScrollRef.current) return
    const scroller = event.currentTarget
    const cycleHeight = scroller.scrollHeight / 3
    if (!cycleHeight) return
    let nextScrollTop = scroller.scrollTop
    if (scroller.scrollTop < cycleHeight * 0.5) nextScrollTop += cycleHeight
    if (scroller.scrollTop > cycleHeight * 1.5) nextScrollTop -= cycleHeight
    if (nextScrollTop !== scroller.scrollTop) {
      adjustingScrollRef.current = true
      scroller.scrollTop = nextScrollTop
      window.requestAnimationFrame(() => { adjustingScrollRef.current = false })
    }
  }

  const handleWheel = (event) => {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.scrollTop += event.deltaY
  }

  return (
    <div className="w-24">
      <p className="mb-2 text-center text-xs font-semibold text-[#6B7280]">{label}</p>
      <div onScroll={keepScrollLooping} onWheel={handleWheel} className="h-32 sm:h-52 space-y-1 overflow-y-auto overscroll-contain rounded-xl bg-[#F5F5F7] p-1.5">
        {loopedValues.map(({ cycle, item }) => (
          <button
            ref={cycle === 1 && item === selected ? selectedRef : undefined}
            key={`${cycle}-${item}`}
            type="button"
            onClick={() => onSelect(item)}
            className={cn(
              "flex w-full items-center justify-center gap-1 rounded-lg py-2 text-sm transition-colors",
              item === selected ? "bg-black font-semibold text-white" : "text-[#1D1D1F] hover:bg-white"
            )}
          >
            {item === selected ? <Check className="h-3.5 w-3.5" /> : null}{item}
          </button>
        ))}
      </div>
    </div>
  )
}

export { DateTimeInput, parseTypedDate, parseTypedTime, parseTypedValue }
